/**
 * Ordering model for the card grid: drag-and-drop (pointer + the App Grid
 * Control drop slots) and keyboard Move up / Move down, both feeding the same
 * 500ms-debounced silent autosave.
 *
 * Ported from the LifeScribe original's useDragReorder — same custom collision
 * detection (pointerWithin, then rectIntersection over the grid slots, then
 * closestCenter over the cards) and the same "silent on success, Save/Discard
 * bar only on failure" contract — with three corrections:
 *
 *   1. Identity is the catalogue's string key, not a numeric row id.
 *   2. The debounce timer is PER CATALOGUE. The original shared one timeout ref
 *      across all four, so reordering two catalogues inside 500ms dropped the
 *      first save.
 *   3. It does NOT write to the database while merely loading the page. The
 *      original auto-saved whenever its active-first display grouping differed
 *      from the stored order, which turned opening the page into a production
 *      write. Display grouping is now display-only until the admin moves a card.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { aiStylesApi } from './api';
import { KEY_FIELD } from './constants';
import { CatalogueKey, OrderItem, StyleRow } from './types';

export const AUTOSAVE_DELAY_MS = 500;

/**
 * Display order for a catalogue: stored `sort_order` first, then active rows
 * ahead of inactive ones so the top four active rows line up with the app's 2×2
 * grid and the "not in app grid" divider falls in the right place.
 */
export function deriveOrder(rows: StyleRow[], catalogue: CatalogueKey): OrderItem[] {
  const field = KEY_FIELD[catalogue];
  const mapped: OrderItem[] = rows.map((row, index) => ({
    key: String(row[field] ?? row.genre_key ?? row.author_key ?? row.style_key ?? index),
    name: row.display_name,
    isActive: Boolean(row.is_active),
  }));
  const sorted = [...rows]
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const ao = a.row.sort_order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.row.sort_order ?? Number.MAX_SAFE_INTEGER;
      return ao === bo ? a.index - b.index : ao - bo;
    })
    .map(({ index }) => mapped[index]);

  return [...sorted.filter((i) => i.isActive), ...sorted.filter((i) => !i.isActive)];
}

export interface UseDragReorderParams {
  /** Called after a successful save so the page can report it if it wants to. */
  onSaved?: (catalogue: CatalogueKey) => void;
}

export function useDragReorder({ onSaved }: UseDragReorderParams = {}) {
  const [pendingOrder, setPendingOrder] = useState<Partial<Record<CatalogueKey, OrderItem[]>>>({});
  const [unsavedCatalogues, setUnsavedCatalogues] = useState<Set<CatalogueKey>>(new Set());
  const [savingCatalogue, setSavingCatalogue] = useState<CatalogueKey | null>(null);
  const [activeDragName, setActiveDragName] = useState<string | null>(null);
  const [activeDragKey, setActiveDragKey] = useState<string | null>(null);

  const lastOverIdRef = useRef<string | number | null>(null);
  const timersRef = useRef<Partial<Record<CatalogueKey, ReturnType<typeof setTimeout>>>>({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach((t) => t && clearTimeout(t));
    },
    []
  );

  const collisionDetection: CollisionDetection = useMemo(
    () => (args) => {
      const gridContainers = args.droppableContainers.filter((c) =>
        String(c.id).startsWith('grid-slot-')
      );
      const cardContainers = args.droppableContainers.filter(
        (c) => !String(c.id).startsWith('grid-slot-')
      );

      const pointerHits = pointerWithin(args);
      if (pointerHits.length > 0) {
        const gridHits = pointerHits.filter((c) => String(c.id).startsWith('grid-slot-'));
        return gridHits.length > 0 ? gridHits : pointerHits;
      }

      if (gridContainers.length > 0) {
        const gridRects = rectIntersection({ ...args, droppableContainers: gridContainers });
        if (gridRects.length > 0) return gridRects;
      }
      if (cardContainers.length > 0) {
        return closestCenter({ ...args, droppableContainers: cardContainers });
      }
      return closestCenter(args);
    },
    []
  );

  const persist = useCallback(
    async (catalogue: CatalogueKey, items: OrderItem[]) => {
      const payload = items.map((item, index) => ({ key: item.key, sort_order: index + 1 }));
      await aiStylesApi.reorder(catalogue, payload);
    },
    []
  );

  /** Debounced, silent on success; on failure raise the Save/Discard bar. */
  const autoSave = useCallback(
    (catalogue: CatalogueKey, items: OrderItem[]) => {
      const existing = timersRef.current[catalogue];
      if (existing) clearTimeout(existing);
      timersRef.current[catalogue] = setTimeout(() => {
        persist(catalogue, items)
          .then(() => {
            setUnsavedCatalogues((prev) => {
              if (!prev.has(catalogue)) return prev;
              const next = new Set(prev);
              next.delete(catalogue);
              return next;
            });
            onSaved?.(catalogue);
          })
          .catch(() => {
            setUnsavedCatalogues((prev) => new Set(prev).add(catalogue));
          });
      }, AUTOSAVE_DELAY_MS);
    },
    [persist, onSaved]
  );

  const getOrdered = useCallback(
    (catalogue: CatalogueKey, rows: StyleRow[]): OrderItem[] => {
      const pending = pendingOrder[catalogue];
      if (!pending) return deriveOrder(rows, catalogue);
      // Reconcile: drop keys that no longer exist, append keys that appeared.
      const derived = deriveOrder(rows, catalogue);
      const live = new Set(derived.map((i) => i.key));
      const byKey = new Map(derived.map((i) => [i.key, i]));
      const kept = pending.filter((i) => live.has(i.key)).map((i) => byKey.get(i.key) as OrderItem);
      const keptKeys = new Set(kept.map((i) => i.key));
      return [...kept, ...derived.filter((i) => !keptKeys.has(i.key))];
    },
    [pendingOrder]
  );

  const applyOrder = useCallback(
    (catalogue: CatalogueKey, items: OrderItem[]) => {
      setPendingOrder((prev) => ({ ...prev, [catalogue]: items }));
      autoSave(catalogue, items);
    },
    [autoSave]
  );

  /** Keyboard/AT reorder — same persistence path as a pointer drag. */
  const moveByKey = useCallback(
    (catalogue: CatalogueKey, rows: StyleRow[], key: string, direction: -1 | 1) => {
      const items = getOrdered(catalogue, rows);
      const from = items.findIndex((i) => i.key === key);
      const to = from + direction;
      if (from === -1 || to < 0 || to >= items.length) return;
      applyOrder(catalogue, arrayMove(items, from, to));
    },
    [getOrdered, applyOrder]
  );

  const handleDragStart = useCallback((event: DragStartEvent, items: OrderItem[]) => {
    lastOverIdRef.current = null;
    const activeId = String(event.active.id);
    if (activeId.startsWith('grid-drag-')) {
      const slotIndex = Number(activeId.replace('grid-drag-', ''));
      const item = items.filter((i) => i.isActive)[slotIndex];
      setActiveDragKey(item?.key ?? null);
      setActiveDragName(item?.name ?? null);
      return;
    }
    const item = items.find((i) => i.key === activeId);
    setActiveDragKey(activeId);
    setActiveDragName(item?.name ?? null);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    if (event.over) lastOverIdRef.current = event.over.id;
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveDragKey(null);
    setActiveDragName(null);
    lastOverIdRef.current = null;
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent, catalogue: CatalogueKey, rows: StyleRow[]) => {
      setActiveDragKey(null);
      setActiveDragName(null);

      const { active, over, collisions } = event;
      let overId: string | number | null = over?.id ?? null;
      if (!overId && collisions && collisions.length > 0) overId = collisions[0].id;
      if (!overId) overId = lastOverIdRef.current;

      // If the last highlighted target was a grid slot, honour it — that is what
      // the admin saw light up, whatever the final collision pass returned.
      const lastWasGrid =
        lastOverIdRef.current != null && String(lastOverIdRef.current).startsWith('grid-slot-');
      const currentIsGrid = overId != null && String(overId).startsWith('grid-slot-');
      if (lastWasGrid && !currentIsGrid) overId = lastOverIdRef.current;

      lastOverIdRef.current = null;
      if (overId == null) return;

      const items = getOrdered(catalogue, rows);
      const activeItems = items.filter((i) => i.isActive);

      const activeIdStr = String(active.id);
      let fromIndex: number;
      if (activeIdStr.startsWith('grid-drag-')) {
        const slot = Number(activeIdStr.replace('grid-drag-', ''));
        const sourceItem = activeItems[slot];
        if (!sourceItem) return;
        fromIndex = items.findIndex((i) => i.key === sourceItem.key);
      } else {
        fromIndex = items.findIndex((i) => i.key === activeIdStr);
      }

      const overIdStr = String(overId);
      let toIndex: number;
      if (overIdStr.startsWith('grid-slot-') || overIdStr.startsWith('grid-drag-')) {
        const slot = Number(overIdStr.replace(/^grid-(slot|drag)-/, ''));
        const targetItem = activeItems[slot];
        toIndex = targetItem
          ? items.findIndex((i) => i.key === targetItem.key)
          : Math.min(slot, items.length - 1);
      } else {
        toIndex = items.findIndex((i) => i.key === overIdStr);
      }

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
      applyOrder(catalogue, arrayMove(items, fromIndex, toIndex));
    },
    [getOrdered, applyOrder]
  );

  /** Manual retry from the failure bar. */
  const saveOrder = useCallback(
    async (catalogue: CatalogueKey, rows: StyleRow[]) => {
      const items = getOrdered(catalogue, rows);
      setSavingCatalogue(catalogue);
      try {
        await persist(catalogue, items);
        setUnsavedCatalogues((prev) => {
          const next = new Set(prev);
          next.delete(catalogue);
          return next;
        });
        return { ok: true as const };
      } catch (e) {
        return { ok: false as const, message: e instanceof Error ? e.message : 'Failed to save order.' };
      } finally {
        setSavingCatalogue(null);
      }
    },
    [getOrdered, persist]
  );

  /** Throw the local ordering away and fall back to what the server sent. */
  const discardOrder = useCallback((catalogue: CatalogueKey) => {
    const timer = timersRef.current[catalogue];
    if (timer) clearTimeout(timer);
    setPendingOrder((prev) => {
      const next = { ...prev };
      delete next[catalogue];
      return next;
    });
    setUnsavedCatalogues((prev) => {
      const next = new Set(prev);
      next.delete(catalogue);
      return next;
    });
  }, []);

  return {
    sensors,
    collisionDetection,
    activeDragKey,
    activeDragName,
    unsavedCatalogues,
    savingCatalogue,
    getOrdered,
    moveByKey,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    saveOrder,
    discardOrder,
  };
}

export default useDragReorder;
